/**
 * Client de l'API du parcours signataire.
 *
 * Les types suivent EXACTEMENT `schemas.ViewResponse` cote backend. Si un
 * champ change la-bas, le typage doit casser ici - c'est le but.
 */

export type SignatureLevel = "simple" | "advanced" | "qualified" | "seal";
export type OtpChannel = "email" | "sms";
export type MethodeSignature = "nom" | "trace" | "image";
/** Un trait = une liste de points, en fraction de la zone de dessin. */
export type Trait = { x: number; y: number }[];

export type Reglage = "autoriser" | "demander" | "interdire";

/** Lot 2bis : une demande du signataire au client (transmission, nom). */
export interface Demande {
  id: string;
  type: "transmission" | "nom";
  statut: "en_attente" | "acceptee" | "refusee";
  /** Transmission : la personne indiquée ; nom : le nom vérifié demandé. */
  nouveau: { nom: string; email: string } | string;
  demandee_le: string;
  decidee_le: string | null;
}

export interface SignerView {
  envelope_id: string;
  title: string;

  /** L'emetteur. Le destinataire ne connait pas Sign365, il connait notre
   *  client : c'est ce nom qui est mis en avant a l'ecran. */
  issuer_name: string | null;
  message_subject: string | null;
  message_body: string | null;

  langue: string | null;
  expires_at: string | null;
  signature_level: SignatureLevel | null;

  signer_name: string | null;
  signer_order: number | null;
  signer_count: number | null;

  /** 15/10/2026 : signature avancée par clé d'accès - remplace l'étape du code. */
  cle_acces?: boolean;
  /** Lot 2 (15/10/2026) : compte ET clé déjà créés. Sinon l'inscription
   *  (compte, vérification d'identité, clé) passe AVANT le document. */
  cle_prete?: boolean;
  /** Lot 2bis (15/10/2026) : réglages de CETTE ligne (autoriser / demander /
   *  interdire), transmissions encore possibles (2 au plus), numéro de mobile
   *  à demander (code par SMS), dernière demande faite depuis ce lien. */
  transmission?: Reglage;
  transmissions_restantes?: number;
  transmission_telephone?: boolean;
  /** Lot 2ter : l'émetteur a refusé une transmission de cette ligne ; on ne la redemande plus. */
  transmission_refusee?: boolean;
  correction_nom?: Reglage;
  demande?: Demande | null;
  otp_channel: OtpChannel | null;
  /** Coordonnee masquee : permet de verifier qu'on ecrit au bon endroit
   *  sans la divulguer a quelqu'un qui aurait intercepte le lien. */
  otp_destination_masked: string | null;
  /** Signataire en SMS sans numero : la demande de code echouerait. */
  otp_destination_missing: boolean;

  /**
   * Texte de consentement servi par le serveur, avec sa version : c'est
   * lui qui l'enregistre dans la piste d'audit, il doit donc etre celui
   * qui est affiche. Optionnel : un serveur anterieur ne le renvoie pas.
   */
  consentement?: {
    version: string;
    corps: string;
    case: string;
    complet: string;
  };
  /** B-18 : lecture deja enregistree cote serveur. Un signataire qui
   *  revient sur son lien ne refait pas defiler tout le document. */
  reviewed?: boolean;
  /** B-17 : visuel deja choisi - on ne le redemande pas au retour. */
  signature_methode?: MethodeSignature | null;
  /**
   * La police manuscrite couvre-t-elle son nom ? Le PDF retombe sur une
   * police ordinaire sinon, et l'apercu doit montrer ce que le document
   * portera - pas une autre police, pas des carres.
   */
  nom_manuscrit_possible?: boolean;
  // 'visionneuse' n'est plus emis depuis B-19, mais le serveur le renvoie
  // encore pour les lectures enregistrees avant : ce type decrit ce qu'on
  // RECOIT, pas ce qu'on envoie.
  review_mode?: "visionneuse" | "declaration" | "defilement" | null;
  consent_given: boolean;
  signed: boolean;
  /** B-08 lot 2 : toutes les signatures faites, le document signe existe.
   *  (Retire de la reponse HTTP par le serveur jusqu'au 25/09/2026.) */
  envelope_completed?: boolean;
  /** Statut de l'enveloppe : 'cancelled', 'declined' (par un autre
   *  signataire) ou 'expired' closent le parcours (06/10/2026). */
  envelope_status?: string | null;
  /** Le dossier de preuve est telechargeable. Il se construit APRES la fin
   *  de l'enveloppe (scellement, parfois plusieurs minutes) : tant qu'il
   *  manque, son telechargement repond 409 (25/09/2026). */
  proof_ready?: boolean;
  declined: boolean;
  signer_status: string | null;
  /** Autres signataires pas encore passes : tant qu'il en reste, le
   *  document signe n'existe pas. */
  other_signers_pending: number;
  /** B-28 : dernieres pages du PDF (l'annexe) a ne pas montrer avant la
   *  signature ; la lecture exigee s'arrete avant elles. */
  pages_annexe: number;
  /** Ses cadres sur les pages du client, avec leur type - que le PDF ne
   *  porte pas. Le bloc de la page de signatures n'y est pas. */
  emplacements: Emplacement[];
  /** B-13 (12/10/2026) : « approver » approuve sans signer. */
  role?: "signer" | "approver";
}

export type TypeEmplacement = "signature" | "date" | "nom" | "email" | "texte" | "mention" | "case"
  | "paraphe" | "radio" | "texte_fixe" | "heure" | "date_heure" | "liste" | "piece_jointe" | "date_saisie";

/** Lot 3bis : les champs que le signataire remplit lui-meme. */
export const CHAMPS_A_SAISIR: readonly TypeEmplacement[] = ["texte", "mention", "case", "paraphe", "radio", "liste", "piece_jointe", "date_saisie"];

/** Porteur, 06/10/2026 : « a quoi sert de valider mon nom ou une date que je
 *  ne peux pas changer ? » - ces cadres s'AFFICHENT, sans action. */
export const CHAMPS_AFFICHES: readonly TypeEmplacement[] = ["date", "heure", "date_heure", "nom", "email", "texte_fixe"];

/** Case, option, paraphe : l'intitule pose par l'emetteur, s'il y en a un
 *  (B-28, 11/10/2026 : une option sans son intitule n'etait qu'un rond). */
const AVEC_INTITULE: readonly TypeEmplacement[] = ["case", "radio", "paraphe", "texte", "liste", "piece_jointe"];
export const nomDuRepere = (libelles: Record<TypeEmplacement, string>, r: { kind: TypeEmplacement; libelle?: string | null }) =>
  (AVEC_INTITULE.includes(r.kind) && r.libelle?.trim()) || libelles[r.kind];

/** Le paraphe : premiere lettre de chaque mot du nom (meme regle que le serveur). */
export const initiales = (nom: string) =>
  nom.replace(/-/g, " ").split(/\s+/).filter((m) => m && !m.includes("@"))
    .map((m) => m[0]!.toUpperCase()).join("").slice(0, 4) || nom.slice(0, 2).toUpperCase();

export interface Emplacement {
  id: string;
  kind: TypeEmplacement;
  libelle: string | null;
  obligatoire: boolean;
  groupe: string | null;
  /** Liste deroulante : les choix proposes. */
  options?: string[] | null;
  valeur: string | null;
  page_no: number;
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Chaque code HTTP du parcours appelle une reaction DIFFERENTE a l'ecran :
 * un lien expire n'est pas un lien inconnu, et un plafond de demandes
 * n'est pas une panne. Les confondre laisserait le signataire sans issue.
 */
export type ApiErrorKind =
  | "not_found" // 404 - lien inconnu ou revoque
  | "expired" // 410 - LIEN expire : le signataire ne peut rien y faire
  | "code_expired" // 410 sur /otp/verify - le CODE a expire : on en redemande un
  | "no_code" // 409 sur /otp/verify - aucun code demande
  | "rate_limited" // 429 - trop de demandes de code
  | "channel_unavailable" // 502 - canal OTP non configure
  | "invalid_code" // 401 - code faux
  | "conflict" // 409 - etape deja franchie
  | "partial_review" // 422 sur /reviewed - il reste des pages a afficher
  | "review_required" // 428 sur /consent - le document n'a pas ete affiche
  | "signature_invalide" // 422 sur /signature - trace ou image inexploitable
  | "signature_requise" // 428 sur /otp - aucun visuel choisi
  | "network" // pas de reponse du tout
  | "server"; // 5xx

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status: number;
  readonly detail: string | null;

  constructor(kind: ApiErrorKind, status: number, detail: string | null) {
    super(detail ?? kind);
    this.kind = kind;
    this.status = status;
    this.detail = detail;
  }
}

/**
 * Un meme code HTTP ne veut pas dire la meme chose partout, et la reaction
 * utile en depend :
 *
 *   410 sur /sign/{token}      -> le LIEN a expire. Le signataire ne peut
 *                                 rien y faire, il doit contacter l'emetteur.
 *   410 sur /otp/verify        -> le CODE a expire (ou les tentatives sont
 *                                 epuisees). Il peut en redemander un.
 *   409 sur /otp/verify        -> aucun code n'a ete demande.
 *   409 sur /consent, /signed  -> etape deja franchie / pas encore prete.
 *
 * Les confondre laisserait le signataire sans issue - ou l'enverrait
 * demander de l'aide alors qu'il pouvait se debloquer seul.
 */
type Context = "otp_verify" | "reviewed" | "signature" | "default";

function kindFor(status: number, detail: string | null, context: Context): ApiErrorKind {
  switch (status) {
    case 404:
      return "not_found";
    case 410:
      return context === "otp_verify" ? "code_expired" : "expired";
    case 429:
      return "rate_limited";
    case 409:
      return context === "otp_verify" ? "no_code" : "conflict";
    // B-18 : le document n'a pas ete affiche en entier. « Precondition
    // Required » et non un conflit : une etape ANTERIEURE manque, le site
    // doit y renvoyer le signataire plutot qu'afficher une panne.
    case 428:
      return context === "otp_verify" ? "signature_requise" : "review_required";
    // 401 : c'est le code que le backend renvoie pour un code OTP faux.
    case 401:
    case 400:
    case 422:
      // 422 sur /reviewed n'est pas un code faux : il reste des pages a
      // afficher. Les confondre afficherait « ce code est incorrect » a
      // quelqu'un qui n'a pas encore vu de code.
      if (context === "reviewed") return "partial_review";
      // B-17 : 422 sur /signature n'est pas un code faux - c'est le visuel
      // qui n'est pas exploitable, et le detail du serveur dit pourquoi.
      if (context === "signature") return "signature_invalide";
      return "invalid_code";
    case 502:
      return "channel_unavailable";
    default:
      // Le backend renvoie 502 quand le canal OTP n'est pas joignable,
      // mais un relais qui tombe peut aussi produire un 500 : on lit le
      // detail plutot que de supposer.
      if (status >= 500 && detail && /canal|sms|relais|smtp/i.test(detail)) {
        return "channel_unavailable";
      }
      return "server";
  }
}

async function request(path: string, init?: RequestInit, context: Context = "default"): Promise<Response> {
  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      headers: { Accept: "application/json", ...(init?.headers ?? {}) },
    });
  } catch {
    // Perte de reseau : distinguee d'une erreur serveur car la reaction
    // utile n'est pas la meme (reessayer vs contacter l'emetteur).
    throw new ApiError("network", 0, null);
  }

  if (!response.ok) {
    let detail: string | null = null;
    try {
      const body = await response.json();
      detail = typeof body?.detail === "string" ? body.detail : null;
    } catch {
      // Corps non-JSON : on garde le statut, qui suffit a decider.
    }
    throw new ApiError(kindFor(response.status, detail, context), response.status, detail);
  }
  return response;
}

/** Le token vient de l'URL. Il n'est JAMAIS place en parametre de requete :
 *  les query strings finissent dans les journaux des reverse proxies, et
 *  ce token vaut droit de signer. */
const base = (token: string) => `/api/sign/${encodeURIComponent(token)}`;

export async function fetchView(token: string): Promise<SignerView> {
  const response = await request(base(token));
  return (await response.json()) as SignerView;
}

/** PDF a signer, recupere en blob et affiche localement - jamais confie a
 *  une visionneuse tierce. */
export async function fetchDocument(token: string): Promise<ArrayBuffer> {
  const response = await request(`${base(token)}/document`);
  return await response.arrayBuffer();
}

/** B-23 : ce que la signature posera dans un de ses emplacements (PDF).
 *  « bloc » = le cadre de la page de signatures ; w et h en points. */
export async function fetchApercu(token: string, kind: string, w: number, h: number): Promise<ArrayBuffer> {
  const q = new URLSearchParams({ kind });
  if (kind !== "bloc") {
    q.set("w", w.toFixed(2));
    q.set("h", h.toFixed(2));
  }
  return await (await request(`${base(token)}/apercu?${q}`)).arrayBuffer();
}

export async function fetchSignedDocument(token: string): Promise<Blob> {
  return await (await request(`${base(token)}/signed`)).blob();
}

export async function fetchProofBundle(token: string): Promise<Blob> {
  return await (await request(`${base(token)}/proof-bundle`)).blob();
}

/**
 * B-18 : ce que le navigateur a reellement affiche.
 *
 * On n'envoie PAS le nombre total de pages : le serveur le compte sur le
 * document. Le lui laisser reduirait le blocage a une formalite - il
 * suffirait d'annoncer un document d'une page.
 */
export async function postDocumentReviewed(
  token: string,
  pages: number[],
  // 'defilement' depuis B-19 : la visionneuse n'affiche plus une page a la
  // fois, le document defile d'un seul tenant et le serveur exige que
  // chaque page ait ete affichee (21/10/2026). 'visionneuse' n'est plus emis - il reste
  // ici parce que des lectures deja enregistrees le portent.
  mode: "defilement" | "declaration",
): Promise<{ pages: number; pages_total: number; mode: string }> {
  const response = await request(
    `${base(token)}/reviewed`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ pages, mode }),
    },
    "reviewed",
  );
  return await response.json();
}

/**
 * B-17 : le visuel de signature choisi.
 *
 * Multipart pour tout le monde, y compris le trace : la requete l'est deja
 * a cause de l'image, et deux formes d'envoi feraient deux chemins de
 * validation cote serveur. Le trace part en JSON dans un champ de
 * formulaire - ce sont des POINTS, pas des pixels : le serveur les
 * redessine en traits dans le PDF.
 */
export async function postSignature(
  token: string,
  methode: MethodeSignature,
  options: { trace?: Trait[]; image?: File } = {},
): Promise<{ methode: MethodeSignature }> {
  const corps = new FormData();
  corps.append("methode", methode);
  if (options.trace) corps.append("trace", JSON.stringify(options.trace));
  if (options.image) corps.append("image", options.image);
  // Pas de Content-Type pose a la main : le navigateur doit y mettre la
  // frontiere multipart, que nous ne connaissons pas.
  const reponse = await request(`${base(token)}/signature`, { method: "POST", body: corps }, "signature");
  return await reponse.json();
}

export async function postConsent(token: string): Promise<void> {
  await request(`${base(token)}/consent`, { method: "POST" });
}

/** Lot 3bis : remplit (ou efface, valeur vide) un de ses champs a saisir. */
export async function postChamp(token: string, champId: string, valeur: string | null): Promise<void> {
  await request(`${base(token)}/champs/${champId}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ valeur }),
  });
}

/** Piece jointe demandee (12/10/2026) : PDF, JPEG ou PNG, 10 Mo. */
export async function postPiece(token: string, champId: string, fichier: File): Promise<void> {
  const corps = new FormData();
  corps.append("fichier", fichier);
  await request(`${base(token)}/champs/${champId}/fichier`, { method: "POST", body: corps });
}

/** Compte gratuit apres la signature (porteur, 12/10/2026) : le mot de passe
 *  seul, l'adresse est celle du signataire. */
export async function compteExistant(token: string): Promise<boolean> {
  const r = await request(`${base(token)}/compte`);
  return Boolean((await r.json()).compte_existant);
}

export async function creerCompte(token: string, password: string): Promise<void> {
  await request(`${base(token)}/compte`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ password }),
  });
}

export async function requestOtp(token: string): Promise<void> {
  await request(`${base(token)}/otp/request`, { method: "POST" });
}

export async function verifyOtp(token: string, code: string): Promise<void> {
  await request(
    `${base(token)}/otp/verify`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code }),
    },
    "otp_verify",
  );
}

// --- Signature avancée par clé d'accès (15/10/2026) -----------------------

export interface EtatCle {
  inscrit: boolean;
  disponible: boolean;
  /** Lot 2 (15/10/2026) : l'inscription commence par le compte. */
  compte_existant?: boolean;
  /** Compte créé, ou mot de passe saisi, sur CE lien. */
  compte_prouve?: boolean;
  /** Compte ET clé : le parcours de signature s'ouvre. */
  pret?: boolean;
  /** 15/10/2026 : une clé existe pour cette adresse, mais à un autre nom que la ligne. */
  cle_autre_nom?: string | null;
  nom_attendu?: string | null;
  /** Lot 2bis : le nom vérifié (pièce ou clé) qui diffère du nom de la ligne. */
  nom_verifie?: string | null;
  correction_nom?: Reglage;
  demande?: Demande | null;
  inscription: {
    statut: "pvid" | "verifiee" | "cle_creee" | "echouee";
    lien: string | null;
    /** Identité vérifiée mais différente du nom indiqué par l'émetteur. */
    motif?: "nom_different" | null;
    nom_attendu?: string | null;
  } | null;
}

const b64u = (buf: ArrayBuffer) =>
  btoa(String.fromCharCode(...new Uint8Array(buf))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
const deB64u = (s: string) =>
  Uint8Array.from(atob(s.replace(/-/g, "+").replace(/_/g, "/")), (c) => c.charCodeAt(0));

async function postJson(path: string, body?: unknown): Promise<Response> {
  return request(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

export async function etatCle(token: string): Promise<EtatCle> {
  return (await (await request(`${base(token)}/cle/etat`)).json()) as EtatCle;
}

/** Lot 2 : crée le compte (aucun compte pour cette adresse) ou vérifie le mot
 *  de passe du compte existant. */
export async function prouverCompte(token: string, password: string): Promise<void> {
  await postJson(`${base(token)}/cle/compte`, { password });
}

/** Lot 2bis : la signature passe à une autre personne (« transmise »), ou la
 *  demande attend la réponse de l'émetteur (« demandee »). */
export async function transmettre(
  token: string, personne: { prenom: string; nom: string; email: string; telephone?: string },
): Promise<"transmise" | "demandee"> {
  return ((await (await postJson(`${base(token)}/transmettre`, personne)).json()) as { etat: "transmise" | "demandee" }).etat;
}

/** Lot 2bis : le nom de la ligne prend le nom VÉRIFIÉ (jamais une saisie). */
export async function corrigerNom(token: string): Promise<"corrige" | "demandee"> {
  return ((await (await postJson(`${base(token)}/cle/corriger-nom`)).json()) as { etat: "corrige" | "demandee" }).etat;
}

/** Écran d'attente : répond aussi quand le lien a expiré (transmission acceptée). */
export async function fetchDemande(token: string): Promise<Demande | null> {
  return ((await (await request(`${base(token)}/demande`)).json()) as { demande: Demande | null }).demande;
}

export async function demarrerInscription(token: string, prenom: string, nom: string): Promise<string> {
  return ((await (await postJson(`${base(token)}/cle/inscription`, { prenom, nom })).json()) as { lien: string }).lien;
}

/** Crée la clé d'accès sur l'appareil (Face ID, empreinte ou code) et l'enregistre. */
export async function creerCle(token: string): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const o: any = await (await postJson(`${base(token)}/cle/enregistrement/options`)).json();
  const cred = (await navigator.credentials.create({
    publicKey: {
      ...o,
      challenge: deB64u(o.challenge),
      user: { ...o.user, id: deB64u(o.user.id) },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      excludeCredentials: (o.excludeCredentials ?? []).map((c: any) => ({ ...c, id: deB64u(c.id) })),
    },
  })) as PublicKeyCredential;
  const r = cred.response as AuthenticatorAttestationResponse;
  await request(`${base(token)}/cle/enregistrement`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      id: cred.id, rawId: b64u(cred.rawId), type: cred.type, clientExtensionResults: {},
      response: { clientDataJSON: b64u(r.clientDataJSON), attestationObject: b64u(r.attestationObject),
                  transports: r.getTransports ? r.getTransports() : [] },
    }),
  });
}

/** Signe avec la clé : le défi porte l'empreinte du document présenté et une transaction unique. */
export async function signerAvecCle(token: string): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const o: any = await (await postJson(`${base(token)}/cle/signature/options`)).json();
  const cred = (await navigator.credentials.get({
    publicKey: {
      ...o,
      challenge: deB64u(o.challenge),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      allowCredentials: (o.allowCredentials ?? []).map((c: any) => ({ ...c, id: deB64u(c.id) })),
    },
  })) as PublicKeyCredential;
  const r = cred.response as AuthenticatorAssertionResponse;
  await request(`${base(token)}/cle/signature`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      id: cred.id, rawId: b64u(cred.rawId), type: cred.type, clientExtensionResults: {},
      response: { clientDataJSON: b64u(r.clientDataJSON), authenticatorData: b64u(r.authenticatorData),
                  signature: b64u(r.signature), userHandle: r.userHandle ? b64u(r.userHandle) : null },
    }),
  });
}

export async function decline(token: string, reason: string): Promise<void> {
  await request(`${base(token)}/decline`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ reason }),
  });
}
